import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Briefcase,
  Globe,
  HeartHandshake,
  HeartPulse,
  Leaf,
  Package,
  Rocket,
  Users,
  WalletCards,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSiteConfig } from '@/contexts/SiteConfigContext';

const VALUE_PILLARS = [
  {
    title: 'Venture programs',
    body: 'Structured, execution-first programs that take founders from idea to traction. We combine rigorous curriculum with direct operator support so teams do not just learn, they ship.',
    image: '/images/kenyan_venture_programs.png',
  },
  {
    title: 'Talent and learning',
    body: 'We develop operators, not just founders. Homeland trains product thinkers, engineers, and business leaders who can build and scale across sectors.',
    image: '/images/kenyan_talent_learning.png',
  },
  {
    title: 'Capital and partnerships',
    body: 'We unlock capital through a curated network of investors, corporates, and ecosystem partners. From pilots to distribution, we help ventures move faster than they could alone.',
    image: '/images/kenyan_capital_partnerships.png',
  },
] as const;

const HERO_IMAGES = [
  '/images/nairobi_skyline.png',     // Nairobi skyline at golden hour
  '/images/mombasa_city.png',        // Mombasa coastal city
  '/images/eldoret_city.png',        // Eldoret city center
  '/images/kenyan_venture_programs.png', // Kenyan team collaboration
  '/images/kenyan_talent_learning.png',  // Kenyan tech learning
] as const;

const CORE_VALUES = [
  {
    title: 'Builders over talkers',
    body: 'Ideas are cheap. Progress is measured in what gets built and shipped.',
  },
  {
    title: 'Earned conviction',
    body: 'We do not follow hype cycles. We test, learn, then commit with clarity.',
  },
  {
    title: 'Default to action',
    body: 'Speed matters. We move early, iterate quickly, and learn in the market.',
  },
  {
    title: 'Depth over surface',
    body: 'We go deep into users, markets, and systems to avoid fragile companies.',
  },
  {
    title: 'Long-term alignment',
    body: 'We optimize for outcomes that compound over years, not short-term optics.',
  },
  {
    title: 'High standards, low ego',
    body: 'Feedback is direct, standards stay high, and ego has no place here.',
  },
] as const;

const BUILD_AREAS = [
  { title: 'Financial infrastructure', icon: WalletCards },
  { title: 'Logistics and supply chains', icon: Package },
  { title: 'Climate and energy systems', icon: Leaf },
  { title: 'Health and access to care', icon: HeartPulse },
  { title: 'Education and talent platforms', icon: Briefcase },
] as const;

const WHO_FOR = [
  'Founders at idea or early stage',
  'Builders transitioning into startups',
  'Operators looking to scale impact',
  'Institutions seeking innovation partnerships',
] as const;

const EAST_AFRICA_CITY_IMAGES = [
  {
    city: 'Nairobi, Kenya',
    image: '/images/nairobi_skyline.png',
  },
  {
    city: 'Mombasa, Kenya',
    image: '/images/mombasa_city.png',
  },
  {
    city: 'Eldoret, Kenya',
    image: '/images/eldoret_city.png',
  },
  {
    city: 'Kisumu, Kenya',
    image: 'https://images.unsplash.com/photo-1611348524140-53c9a25263d6?auto=format&fit=crop&w=1400&q=80',
  },
] as const;

function normalizeEastAfricaCityImages(
  arr: unknown[],
): Array<{ city: string; image: string }> {
  const out: Array<{ city: string; image: string }> = [];
  for (const x of arr) {
    if (!x || typeof x !== 'object') continue;
    const image = 'image' in x && typeof (x as { image?: unknown }).image === 'string'
      ? (x as { image: string }).image.trim()
      : '';
    if (!image) continue;
    const city = 'city' in x && typeof (x as { city?: unknown }).city === 'string'
      ? (x as { city: string }).city.trim()
      : '';
    out.push({ city, image });
  }
  return out;
}

function normalizeLogoCarouselItems(arr: unknown[]): { url: string; name?: string }[] {
  const out: { url: string; name?: string }[] = [];
  for (const x of arr) {
    if (typeof x === 'string' && x.trim()) {
      out.push({ url: x.trim() });
      continue;
    }
    if (x && typeof x === 'object' && 'url' in x && typeof (x as { url: unknown }).url === 'string') {
      const url = (x as { url: string }).url.trim();
      if (!url) continue;
      const name = (x as { name?: unknown }).name;
      out.push({
        url,
        name: typeof name === 'string' && name.trim() ? name.trim() : undefined,
      });
    }
  }
  return out;
}

function parseAboutLogoCarouselFromConfig(raw: unknown): {
  title: string | null;
  items: { url: string; name?: string }[];
} {
  if (raw == null) return { title: null, items: [] };
  if (Array.isArray(raw)) {
    return { title: null, items: normalizeLogoCarouselItems(raw) };
  }
  if (typeof raw === 'object' && raw !== null && 'items' in raw && Array.isArray((raw as { items: unknown[] }).items)) {
    const titleRaw = (raw as { title?: unknown }).title;
    const title =
      typeof titleRaw === 'string' && titleRaw.trim() ? titleRaw.trim() : null;
    return {
      title,
      items: normalizeLogoCarouselItems((raw as { items: unknown[] }).items),
    };
  }
  return { title: null, items: [] };
}

export function About() {
  const { config: siteConfig } = useSiteConfig();
  const mission = (siteConfig as any)?.aboutMission;
  const vision = (siteConfig as any)?.aboutVision;
  const heroHeading =
    (siteConfig as any)?.aboutHeroHeading?.trim() ||
    "Building Africa's next generation of enduring companies";
  const heroSubtext =
    (siteConfig as any)?.aboutHeroSubtext?.trim() ||
    "Homeland is an innovation ecosystem designed to turn ambitious ideas into globally competitive ventures. We bring together founders, operators, capital, and institutions to solve Africa's hardest problems at scale.";
  const heroParagraph =
    (siteConfig as any)?.aboutHeroParagraph?.trim() ||
    'Based in Nairobi and connected across the continent, we close the gap between talent and opportunity. We do not just support startups, we help build them from first principles with systems, networks, and execution discipline.';
  const heroImagesFromConfig = Array.isArray((siteConfig as any)?.aboutHeroImages)
    ? (siteConfig as any).aboutHeroImages
        .filter((x: unknown) => typeof x === 'string' && x.trim())
        // Guard against random-source URLs that often fail/CORS and show grey backgrounds
        .filter((x: string) => !x.includes('source.unsplash.com'))
    : [];
  const heroImages = heroImagesFromConfig.length > 0 ? heroImagesFromConfig : HERO_IMAGES;
  const missionImage =
    (siteConfig as any)?.aboutMissionImage?.trim() ||
    'https://images.pexels.com/photos/1181396/pexels-photo-1181396.jpeg?auto=compress&cs=tinysrgb&w=1400';
  const buildImage =
    (siteConfig as any)?.aboutBuildImage?.trim() ||
    '/images/kenyan_capital_partnerships.png';
  const centerLogo = siteConfig?.faviconUrl?.trim() || '/favicon.svg';
  const valuePillars =
    Array.isArray((siteConfig as any)?.aboutValuePillars) && (siteConfig as any).aboutValuePillars.length > 0
      ? (siteConfig as any).aboutValuePillars
      : VALUE_PILLARS;
  const coreValues =
    Array.isArray((siteConfig as any)?.aboutCoreValues) && (siteConfig as any).aboutCoreValues.length > 0
      ? (siteConfig as any).aboutCoreValues
      : CORE_VALUES;
  const buildAreas =
    Array.isArray((siteConfig as any)?.aboutBuildAreas) && (siteConfig as any).aboutBuildAreas.length > 0
      ? (siteConfig as any).aboutBuildAreas
      : BUILD_AREAS.map((x) => x.title);
  const whoFor =
    Array.isArray((siteConfig as any)?.aboutWhoFor) && (siteConfig as any).aboutWhoFor.length > 0
      ? (siteConfig as any).aboutWhoFor
      : WHO_FOR;
  const tractionStats =
    Array.isArray((siteConfig as any)?.aboutTractionStats) && (siteConfig as any).aboutTractionStats.length > 0
      ? (siteConfig as any).aboutTractionStats
      : [
          { value: '25+', label: 'Founders supported' },
          { value: '14', label: 'Ventures launched' },
          { value: '30+', label: 'Partnerships' },
          { value: '8', label: 'Countries connected' },
        ];
  const logoCarousel = parseAboutLogoCarouselFromConfig((siteConfig as any)?.aboutLogoCarousel);
  const eastAfricaCityImagesFromConfig = Array.isArray((siteConfig as any)?.aboutEastAfricaCityImages)
    ? normalizeEastAfricaCityImages((siteConfig as any).aboutEastAfricaCityImages)
    : [];
  const eastAfricaCityImages =
    eastAfricaCityImagesFromConfig.length > 0
      ? eastAfricaCityImagesFromConfig
      : [...EAST_AFRICA_CITY_IMAGES];
  const logoStripItems = logoCarousel.items;
  const showLogoStrip = logoStripItems.length > 0;
  const logoMarqueeItems = showLogoStrip ? [...logoStripItems, ...logoStripItems] : [];
  const [heroIndex, setHeroIndex] = useState(0);
  const [eastAfricaCityIndex, setEastAfricaCityIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroImages.length);
    }, 4500);
    return () => window.clearInterval(id);
  }, [heroImages.length]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setEastAfricaCityIndex((prev) => (prev + 1) % eastAfricaCityImages.length);
    }, 3800);
    return () => window.clearInterval(id);
  }, [eastAfricaCityImages.length]);

  return (
    <div className="relative overflow-hidden">
      {/* Hero */}
      <section className="relative flex min-h-[72vh] flex-col">
        <div className="absolute inset-0 bg-primary" aria-hidden>
          {heroImages.map((image: string, idx: number) => (
            <div
              key={image}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                idx === heroIndex ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                backgroundImage: `linear-gradient(110deg, rgba(5,10,20,0.82) 25%, rgba(5,10,20,0.52) 55%, rgba(5,10,20,0.18) 100%), url('${image}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          ))}
        </div>
        <div className="relative z-10 flex min-h-[72vh] flex-1 flex-col">
          <div className="flex flex-1 items-end px-6 pb-10 pt-20 md:pb-12 md:pt-28">
            <div className="mx-auto w-full max-w-6xl">
              <div className="max-w-3xl">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-africa-yellow">
                  About Homeland
                </p>
                <h1 className="mb-5 font-display text-4xl font-bold leading-tight text-white md:text-6xl">
                  {heroHeading}
                </h1>
                <p className="mb-4 text-base leading-relaxed text-white/85 md:text-lg">
                  {heroSubtext}
                </p>
                <p className="mb-7 text-sm leading-relaxed text-white/75 md:text-base">
                  {heroParagraph}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    asChild
                    size="lg"
                    className="rounded-xl bg-accent text-accent-foreground hover:bg-gold-light"
                  >
                    <Link to="/courses">
                      Apply to programs <ArrowRight className="ml-1 inline h-5 w-5" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    size="lg"
                    className="rounded-xl border-primary-foreground/55 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
                  >
                    <Link to="/contact">Partner with us</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {showLogoStrip && (
            <div className="relative w-full border-t border-white/15 bg-black/40 py-5 backdrop-blur-md md:py-6">
              {logoCarousel.title && (
                <p className="mb-4 px-6 text-center text-[10px] font-semibold uppercase tracking-[0.25em] text-white/60 md:text-xs">
                  {logoCarousel.title}
                </p>
              )}
              <div className="w-full overflow-hidden">
                <div className="homeland-logo-marquee-track items-center gap-14 md:gap-20">
                  {logoMarqueeItems.map((item, i) => (
                    <div
                      key={`${item.url}-${i}`}
                      className="flex h-11 shrink-0 items-center justify-center px-3 md:h-12"
                    >
                      <img
                        src={item.url}
                        alt={item.name || 'Partner logo'}
                        className="max-h-10 w-auto max-w-[min(140px,28vw)] object-contain opacity-90 transition hover:opacity-100 md:max-h-11"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Mission / vision */}
      <section className="py-14 md:py-20 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-7 rounded-2xl overflow-hidden border border-border min-h-[320px]">
            <img
              src={missionImage}
              alt="African founders collaborating"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="md:col-span-5 rounded-2xl border border-border bg-card p-6 md:p-7 space-y-5">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">
                Mission
              </p>
              <p className="text-sm text-foreground leading-relaxed">
                {mission ??
                  'To design high-performance environments where African founders can build world-class companies from zero to scale without leaving the continent.'}
              </p>
            </div>
            <div className="h-px bg-border" />
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">
                Vision
              </p>
              <p className="text-sm text-foreground leading-relaxed">
                {vision ??
                  'A continent where talent, capital, and execution converge to produce companies that define global markets, not just participate in them.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How we create value */}
      <section className="py-14 md:py-18 px-6 bg-muted/40">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground mb-7">
            How Homeland creates value
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {valuePillars.map((item: any) => (
              <article key={item.title} className="rounded-2xl overflow-hidden border border-border bg-card">
                <div className="aspect-[16/10] w-full">
                  <img src={item.image} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <h3 className="font-display font-semibold mb-2 text-foreground text-lg">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* What we build */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:items-center">
          <div className="lg:col-span-7">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-africa-orange mb-2">
              What We Actually Build
            </p>
            <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground mb-4">
              Foundational companies in high-impact sectors
            </h2>
            <p className="text-sm md:text-base text-muted-foreground mb-6 leading-relaxed">
              We focus on sectors where Africa has structural opportunity. The goal is not just
              startups, but durable companies that reshape industries.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {buildAreas.map((item: any, idx: number) => {
                const Icon = BUILD_AREAS[idx % BUILD_AREAS.length].icon;
                const label = typeof item === 'string' ? item : item?.title ?? '';
                return (
                  <div key={`${label}-${idx}`} className="rounded-xl border border-border bg-card px-4 py-3 flex items-center gap-3">
                    <Icon className="w-4 h-4 text-africa-green" />
                    <span className="text-sm text-foreground">{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="lg:col-span-5 rounded-2xl overflow-hidden border border-border h-full min-h-[320px]">
            <img
              src={buildImage}
              alt="Operational systems and data"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Core values */}
      <section className="py-16 md:py-20 px-6 bg-muted/40">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-africa-orange mb-2">
              Core Values
            </p>
            <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground mb-3">
              How we operate
            </h2>
            <p className="text-sm text-muted-foreground max-w-3xl">
              These are not slogans. They define how we build, who we work with, and what we do
              not compromise on.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {coreValues.map((item: any, idx: number) => (
              <div key={`${item?.title ?? 'value'}-${idx}`} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-display font-semibold text-foreground text-lg mb-2">{item?.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item?.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it is for + traction */}
      <section className="py-16 px-6 bg-muted/40">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground mb-3">
              Who joins Homeland
            </h2>
            <p className="text-sm text-muted-foreground mb-5">
              If you are serious about building, you belong here.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {whoFor.map((item: any, idx: number) => (
                <div key={`${typeof item === 'string' ? item : item?.label ?? 'who'}-${idx}`} className="rounded-xl border border-border bg-card px-4 py-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-africa-blue" />
                  <span className="text-sm text-foreground">{typeof item === 'string' ? item : item?.label ?? ''}</span>
                </div>
              ))}
            </div>

            <h3 className="text-xl font-display font-semibold text-foreground mb-3">Early momentum</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {tractionStats.map((stat: any, idx: number) => (
                <div key={`${stat?.label ?? 'stat'}-${idx}`} className="rounded-xl border border-border bg-card p-4">
                  <p className="text-2xl font-display font-bold text-foreground">{stat?.value ?? ''}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat?.label ?? ''}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 rounded-2xl border border-border min-h-[420px] bg-card/90 p-6 flex flex-col justify-between relative overflow-hidden">
            <div
              aria-hidden
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 18% 20%, rgba(245,166,35,0.32) 0, transparent 30%), radial-gradient(circle at 82% 15%, rgba(46,134,222,0.28) 0, transparent 28%), radial-gradient(circle at 70% 80%, rgba(39,174,96,0.30) 0, transparent 34%)',
              }}
            />
            <div className="relative">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-africa-orange mb-3">Builder Network</p>
              <h3 className="text-2xl font-display font-semibold text-foreground mb-2">
                Founders. Operators. Institutions.
              </h3>
              <p className="text-sm text-muted-foreground">
                A visual map of the people and systems that compound progress across the Homeland ecosystem.
              </p>
            </div>
            <div className="relative mt-6 rounded-xl border border-border bg-background/70 p-4">
              <div className="relative h-[210px]">
                <svg viewBox="0 0 320 210" className="absolute inset-0 w-full h-full" aria-hidden>
                  <defs>
                    <linearGradient id="g1" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#f5a623" stopOpacity="0.9" />
                      <stop offset="100%" stopColor="#2e86de" stopOpacity="0.85" />
                    </linearGradient>
                  </defs>
                  <path d="M52 44 C92 34, 126 58, 160 100" stroke="url(#g1)" strokeWidth="2.3" strokeOpacity="0.8" fill="none" strokeLinecap="round" />
                  <path d="M268 44 C228 34, 194 58, 160 100" stroke="url(#g1)" strokeWidth="2.3" strokeOpacity="0.8" fill="none" strokeLinecap="round" />
                  <path d="M66 168 C102 150, 132 126, 160 100" stroke="url(#g1)" strokeWidth="2.3" strokeOpacity="0.8" fill="none" strokeLinecap="round" />
                  <path d="M254 168 C218 150, 188 126, 160 100" stroke="url(#g1)" strokeWidth="2.3" strokeOpacity="0.8" fill="none" strokeLinecap="round" />
                  <path d="M52 44 C120 18, 200 18, 268 44" stroke="#2e86de" strokeWidth="1.4" strokeOpacity="0.35" fill="none" strokeDasharray="4 5" />
                  <path d="M66 168 C128 186, 192 186, 254 168" stroke="#27ae60" strokeWidth="1.4" strokeOpacity="0.35" fill="none" strokeDasharray="4 5" />
                </svg>

                <div className="absolute left-4 top-4 w-20 rounded-lg border border-border bg-card px-2 py-2 text-center">
                  <Users className="w-4 h-4 mx-auto text-africa-orange mb-1" />
                  <p className="text-[10px] font-semibold text-foreground">FOUNDERS</p>
                </div>
                <div className="absolute right-4 top-4 w-20 rounded-lg border border-border bg-card px-2 py-2 text-center">
                  <Rocket className="w-4 h-4 mx-auto text-africa-blue mb-1" />
                  <p className="text-[10px] font-semibold text-foreground">OPERATORS</p>
                </div>
                <div className="absolute left-8 bottom-4 w-20 rounded-lg border border-border bg-card px-2 py-2 text-center">
                  <Briefcase className="w-4 h-4 mx-auto text-africa-green mb-1" />
                  <p className="text-[10px] font-semibold text-foreground">PARTNERS</p>
                </div>
                <div className="absolute right-8 bottom-4 w-20 rounded-lg border border-border bg-card px-2 py-2 text-center">
                  <WalletCards className="w-4 h-4 mx-auto text-africa-yellow mb-1" />
                  <p className="text-[10px] font-semibold text-foreground">CAPITAL</p>
                </div>

                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full border border-border bg-card shadow-sm flex items-center justify-center">
                  <img src={centerLogo} alt="Homeland mark" className="w-10 h-10 object-contain" />
                </div>
              </div>
            </div>
            <div className="relative mt-4 grid grid-cols-2 gap-2 text-[11px]">
              <div className="rounded-md border border-border bg-background/60 px-2 py-1.5 text-muted-foreground">
                <span className="font-semibold text-foreground">Idea</span>{' -> '}Build
              </div>
              <div className="rounded-md border border-border bg-background/60 px-2 py-1.5 text-muted-foreground">
                <span className="font-semibold text-foreground">Build</span>{' -> '}Traction
              </div>
              <div className="rounded-md border border-border bg-background/60 px-2 py-1.5 text-muted-foreground">
                <span className="font-semibold text-foreground">Traction</span>{' -> '}Capital
              </div>
              <div className="rounded-md border border-border bg-background/60 px-2 py-1.5 text-muted-foreground">
                <span className="font-semibold text-foreground">Capital</span>{' -> '}Scale
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* From Africa to world */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:items-center">
          <div className="md:col-span-2">
            <h2 className="text-2xl md:text-3xl font-display font-semibold text-foreground mb-3">
              From Africa to the world
            </h2>
            <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
              Homeland starts in Nairobi, but is designed for scale across Africa and beyond. Our
              ecosystem connects founders to regional markets, global capital, and cross-border
              opportunities.
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground mb-5">
              <li className="flex items-center gap-2">
                <Rocket className="w-4 h-4 text-africa-orange" /> Regional partnerships across East
                and West Africa
              </li>
              <li className="flex items-center gap-2">
                <HeartHandshake className="w-4 h-4 text-africa-blue" /> Access to global investor networks
              </li>
              <li className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-africa-green" /> Remote-first collaboration model
              </li>
            </ul>
            <Button asChild variant="outline" size="sm" className="rounded-full">
              <Link to="/ecosystem">Explore the ecosystem</Link>
            </Button>
          </div>
          <div className="rounded-2xl border border-border bg-card/70 p-3 md:p-4">
            <div className="relative rounded-xl overflow-hidden h-[220px] md:h-[260px]">
              {eastAfricaCityImages.map((item, idx) => (
                <img
                  key={`${item.image}-${idx}`}
                  src={item.image}
                  alt={item.city}
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
                    idx === eastAfricaCityIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent p-4">
                <p className="text-sm font-medium text-white">
                  {eastAfricaCityImages[eastAfricaCityIndex]?.city}
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-center gap-1.5">
              {eastAfricaCityImages.map((item, idx) => (
                <span
                  key={`${item.image}-dot-${idx}`}
                  className={`h-1.5 rounded-full transition-all ${
                    idx === eastAfricaCityIndex ? 'w-5 bg-africa-orange' : 'w-1.5 bg-muted-foreground/50'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-display font-semibold text-foreground mb-4">
            Build something that matters
          </h2>
          <p className="text-sm md:text-base text-muted-foreground mb-6">
            Whether you are starting from scratch or scaling something real, Homeland is where
            serious builders go to work.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-gold-light rounded-xl"
            >
              <Link to="/courses">
                Apply to programs <ArrowRight className="w-5 h-5 ml-1 inline" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-xl">
              <Link to="/contact">Partner with us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
